(() => {
  if (window.LashNovaCartTransactions) return;

  const markerProperty = '_lashnova_upgrade_id';
  const buttonSelector = 'form[action*="/cart/add"] button, button[form^="product-form-"], cart-remove-button button, cart-remove-button a, quantity-input button, button[name="checkout"], input[name="checkout"], .cart__checkout-button';
  const readonlySelector = 'input[name="quantity"], input[name="updates[]"]';
  const inertSelector = '.cart__dynamic-checkout-buttons, .additional-checkout-buttons, .shopify-payment-button';
  let activeLock = null;

  const text = (key) => window.LashNovaCartStrings?.[key] || window.cartStrings?.error || '';
  const isNested = (item) => Boolean(item?.parent_relationship?.parent || item?.parent_relationship?.parent_key);
  const propertiesOf = (value) => {
    const entries = Array.isArray(value) ? value : Object.entries(value || {});
    return Object.fromEntries(entries.filter(([key, item]) => item !== null && item !== '').map(([key, item]) => [key, String(item)]).sort(([left], [right]) => left.localeCompare(right)));
  };
  const sellingPlanOf = (item) => Number(item?.selling_plan_allocation?.selling_plan?.id || item?.sellingPlanId || 0);
  const identityOf = (item) => JSON.stringify([
    Number(item?.variant_id || item?.variantId || item?.id),
    propertiesOf(item?.properties),
    sellingPlanOf(item),
  ]);
  const descriptorOf = (item) => ({
    key: item.key,
    variantId: Number(item.variant_id || item.id),
    properties: propertiesOf(item.properties),
    sellingPlanId: sellingPlanOf(item),
  });
  const matchLine = (cart, descriptor) => {
    const matches = (cart?.items || []).filter((item) => !isNested(item) && identityOf(item) === identityOf(descriptor));
    const exact = matches.find((item) => item.key === descriptor.key);
    return exact || (matches.length === 1 ? matches[0] : null);
  };
  const findPlainSingle = (cart, variantId) => {
    const matches = (cart?.items || []).filter((item) => !isNested(item) && Number(item.variant_id || item.id) === Number(variantId) && sellingPlanOf(item) === 0 && Object.keys(propertiesOf(item.properties)).length === 0);
    return matches.length === 1 ? matches[0] : null;
  };
  const canChange = (item) => Number.isInteger(Number(item?.quantity)) && Number(item.quantity) > 0 && item?.instructions?.can_update_quantity !== false && (Number(item.quantity) > 1 || item?.instructions?.can_remove !== false);

  const captureInteraction = (event) => {
    if (!activeLock || !(event.target instanceof Element)) return;
    const target = event.target;
    const isSubmit = event.type === 'submit' && (target.matches('form[action*="/cart/add"], form[action$="/cart"]') || target.id === 'cart' || target.id === 'CartDrawer-Form');
    if (isSubmit || target.closest(`${buttonSelector}, ${readonlySelector}, variant-selects, ${inertSelector}`)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };
  ['click', 'change', 'submit'].forEach((name) => document.addEventListener(name, captureInteraction, true));

  const tryAcquire = ({ kind = 'cart' } = {}) => {
    if (activeLock) return null;
    const token = { kind, controls: [], readonly: [], inert: [] };
    activeLock = token;
    try {
      if (window.CustomEvent) document.dispatchEvent?.(new CustomEvent('lashnova:cart-transaction-start'));
      document.querySelectorAll(buttonSelector).forEach((element) => {
        if ('disabled' in element) {
          token.controls.push([element, element.disabled]);
          element.disabled = true;
        }
      });
      document.querySelectorAll(readonlySelector).forEach((element) => {
        token.readonly.push([element, element.readOnly]);
        element.readOnly = true;
      });
      document.querySelectorAll(inertSelector).forEach((element) => {
        token.inert.push([element, element.hasAttribute('inert')]);
        element.setAttribute('inert', '');
      });
      return token;
    } catch (error) {
      release(token);
      throw error;
    }
  };
  const release = (token) => {
    if (!token || activeLock !== token) return;
    try {
      token.controls.forEach(([element, disabled]) => { if (element.isConnected) element.disabled = disabled; });
      token.readonly.forEach(([element, readonly]) => { if (element.isConnected) element.readOnly = readonly; });
      token.inert.forEach(([element, inert]) => { if (element.isConnected) element.toggleAttribute('inert', inert); });
    } finally {
      activeLock = null;
    }
  };

  class CartRequestError extends Error {
    constructor(message, definitive = false) {
      super(message);
      this.definitive = definitive;
    }
  }
  const requestJson = async (url, options = {}) => {
    let response;
    try {
      response = await fetch(url, {
        credentials: 'same-origin',
        ...options,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...(options.headers || {}) },
      });
    } catch (_error) {
      throw new CartRequestError(text('request_failed'));
    }
    let data;
    try {
      data = await response.json();
    } catch (_error) {
      throw new CartRequestError(text('request_failed'));
    }
    if (!response.ok || data?.status || data?.errors) {
      const message = data?.description || data?.message || data?.errors;
      throw new CartRequestError(typeof message === 'string' ? message : text('request_failed'), response.status >= 400 && response.status < 500);
    }
    return data;
  };
  const readCart = () => requestJson(`${window.routes.cart_url}.js`);
  const post = (url, body) => requestJson(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const readSections = async (sections, sectionsUrl) => {
    if (!sections?.length) return {};
    const separator = sectionsUrl.includes('?') ? '&' : '?';
    try {
      return await requestJson(`${sectionsUrl}${separator}sections=${encodeURIComponent(sections.slice(0, 5).join(','))}`);
    } catch (_error) {
      return {};
    }
  };
  const withSections = async (cart, options) => ({ ...cart, sections: await readSections(options.sections, options.sectionsUrl) });
  const findAdded = (cart, variantId, marker) => {
    const matches = (cart?.items || []).filter((item) => !isNested(item) && Number(item.variant_id || item.id) === variantId && item.properties?.[markerProperty] === marker);
    return matches.length === 1 ? matches[0] : null;
  };
  const sourceQuantity = (cart, descriptor) => {
    const matches = (cart?.items || []).filter((item) => !isNested(item) && identityOf(item) === identityOf(descriptor));
    if (matches.length === 0) return 0;
    const item = matchLine(cart, descriptor);
    return item ? Number(item.quantity) : null;
  };
  const quantityMatches = (cart, sources, decrement) => sources.every(({ descriptor, quantity }) => sourceQuantity(cart, descriptor) === quantity - decrement);
  const uniqueMarker = () => `${Date.now()}-${window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}`;

  const executeBundleUpgrade = async (options) => {
    const token = tryAcquire({ kind: 'bundle-upgrade' });
    if (!token) return { status: 'busy', messageKey: 'busy', verified: false };
    const bundleVariantId = Number(options.bundleVariantId);
    const marker = uniqueMarker();
    const sectionBody = { sections: (options.sections || []).slice(0, 5), sections_url: options.sectionsUrl };
    let latestCart = null;
    let sources = [];
    let addAttempted = false;
    const result = async (status, messageKey, details = {}) => {
      const outcome = {
        status, messageKey, verified: Boolean(latestCart),
        cart: latestCart ? await withSections(latestCart, options) : null,
        sourceCount: sources.length,
        ...details,
      };
      if (options.onResult) await options.onResult(outcome);
      return outcome;
    };
    try {
      if (!Number.isFinite(bundleVariantId) || bundleVariantId <= 0) return await result('unchanged', 'invalid_state');
      latestCart = await readCart();
      if (options.sourceDescriptors) {
        if (options.sourceDescriptors.some((descriptor) => sellingPlanOf(descriptor) !== 0 || Object.keys(propertiesOf(descriptor.properties)).length > 0)) return await result('unchanged', 'invalid_state');
        if ((latestCart.items || []).some((item) => !isNested(item) && Number(item.variant_id || item.id) === bundleVariantId)) return await result('unchanged', 'review_needed');
        const lines = options.sourceDescriptors.map((descriptor) => matchLine(latestCart, descriptor));
        if (!lines.length || lines.some((line) => !line || !canChange(line)) || new Set(lines.map((line) => line.key)).size !== lines.length) return await result('unchanged', 'invalid_state');
        sources = lines.map((line) => ({ descriptor: descriptorOf(line), quantity: Number(line.quantity) }));
      } else if (options.sourceVariantId) {
        const line = findPlainSingle(latestCart, options.sourceVariantId);
        if (line && canChange(line)) sources = [{ descriptor: descriptorOf(line), quantity: Number(line.quantity) }];
      }

      addAttempted = true;
      let addError = null;
      try {
        await post(window.routes.cart_add_url, { ...sectionBody, items: [{ id: bundleVariantId, quantity: 1, properties: { [markerProperty]: marker } }] });
      } catch (error) {
        addError = error;
      }
      latestCart = await readCart();
      const added = findAdded(latestCart, bundleVariantId, marker);
      if (!added) return await result(addError?.definitive ? 'unchanged' : 'review', addError?.definitive ? 'request_failed' : 'review_needed', { error: addError });
      if (Number(added.quantity) !== 1) return await result('review', 'review_needed');
      if (!sources.length) return await result('added', 'bundle_added', { itemKey: added.key });

      const currentSources = sources.map(({ descriptor }) => matchLine(latestCart, descriptor));
      if (currentSources.some((line) => !line || !canChange(line))) return await result('review', 'review_needed', { itemKey: added.key });
      sources = currentSources.map((line) => ({ descriptor: descriptorOf(line), quantity: Number(line.quantity) }));
      const updates = Object.fromEntries(sources.map(({ descriptor, quantity }) => [descriptor.key, quantity - 1]));
      let removalError = null;
      try {
        await post(window.routes.cart_update_url, { ...sectionBody, updates });
      } catch (error) {
        removalError = error;
      }
      latestCart = await readCart();
      const bundleAfterRemoval = findAdded(latestCart, bundleVariantId, marker);
      if (bundleAfterRemoval && Number(bundleAfterRemoval.quantity) === 1 && quantityMatches(latestCart, sources, 1)) return await result('upgraded', 'upgrade_completed', { itemKey: bundleAfterRemoval.key });

      if (!removalError?.definitive || !bundleAfterRemoval || Number(bundleAfterRemoval.quantity) !== 1 || !quantityMatches(latestCart, sources, 0)) return await result('review', 'review_needed', { itemKey: bundleAfterRemoval?.key, error: removalError });

      const rollbackCart = await readCart();
      const rollbackBundle = findAdded(rollbackCart, bundleVariantId, marker);
      if (!rollbackBundle || Number(rollbackBundle.quantity) !== 1 || !quantityMatches(rollbackCart, sources, 0)) {
        latestCart = rollbackCart;
        return await result('review', 'review_needed', { error: removalError });
      }
      try {
        await post(window.routes.cart_change_url, { ...sectionBody, id: rollbackBundle.key, quantity: 0 });
      } catch (_error) {
        latestCart = await readCart();
        const rolledBack = !findAdded(latestCart, bundleVariantId, marker) && quantityMatches(latestCart, sources, 0);
        return await result(rolledBack ? 'unchanged' : 'review', rolledBack ? 'single_styles_kept' : 'review_needed', { error: removalError });
      }
      latestCart = await readCart();
      const rolledBack = !findAdded(latestCart, bundleVariantId, marker) && quantityMatches(latestCart, sources, 0);
      return await result(rolledBack ? 'unchanged' : 'review', rolledBack ? 'single_styles_kept' : 'review_needed', { error: removalError });
    } catch (error) {
      latestCart = null;
      return { status: addAttempted ? 'review' : 'unchanged', messageKey: addAttempted ? 'review_needed' : 'request_failed', verified: false, cart: null, sourceCount: sources.length, error };
    } finally {
      release(token);
    }
  };

  window.LashNovaCartTransactions = { tryAcquire, release, isBusy: () => Boolean(activeLock), executeBundleUpgrade, readCart, readSections, findPlainSingle, text, descriptorOf, matchLine, isNested };
})();
